import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CPM_LICENCE_EXCISE_COPY } from '../../../constants/centralProductMaster.constants';

export type LicensedExciseOption = {
    id: string;
    label: string;
    licensed: boolean;
};

interface LicensedExciseDropdownProps {
    options: LicensedExciseOption[];
    selectedId: string | null;
    onChange: (id: string) => void;
    /** Warning border when create is blocked pending a pick. */
    blocked?: boolean;
    disabled?: boolean;
}

/**
 * Review Detail resolution-bar picker. Visual tokens match the Claude Design
 * prototype (42px trigger, licensed purple tint, `On this store’s licence` badge).
 */
export default function LicensedExciseDropdown({
    options,
    selectedId,
    onChange,
    blocked = false,
    disabled = false,
}: LicensedExciseDropdownProps) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPos, setMenuPos] = useState<{
        top: number;
        left: number;
        width: number;
    } | null>(null);

    const picked = options.find((o) => o.id === selectedId) ?? null;

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            const target = e.target as Node;
            if (rootRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    useEffect(() => {
        if (disabled && open) setOpen(false);
    }, [disabled, open]);

    useLayoutEffect(() => {
        if (!open) {
            setMenuPos(null);
            return;
        }
        const update = () => {
            const trigger = triggerRef.current;
            const menu = menuRef.current;
            if (!trigger) return;
            const rect = trigger.getBoundingClientRect();
            const menuHeight = menu?.offsetHeight ?? 180;
            const gap = 7;
            const top = rect.top - gap - menuHeight;
            const width = Math.max(rect.width, 210);
            let left = rect.left;
            left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
            setMenuPos({ top, left, width });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [open, options]);

    const firstUnlicensedIndex = options.findIndex((o) => !o.licensed);
    const hasLicensed = options.some((o) => o.licensed);

    const triggerBorder = open
        ? 'var(--cpm-brand-purple)'
        : blocked
          ? 'var(--cpm-warning-border)'
          : 'var(--cpm-border-strong)';

    return (
        <div ref={rootRef} className="relative w-[360px] max-w-full">
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled}
                aria-expanded={open}
                aria-haspopup="listbox"
                onClick={() => {
                    if (disabled) return;
                    setOpen((v) => !v);
                }}
                className={[
                    'flex items-center w-full bg-white rounded-[10px] border-[1.5px] text-left transition-all duration-150',
                    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                ].join(' ')}
                style={{
                    height: 42,
                    paddingLeft: 13,
                    paddingRight: 13,
                    gap: 10,
                    borderColor: triggerBorder,
                    boxShadow: open ? '0 0 0 3px rgba(110,100,240,.28)' : undefined,
                }}
            >
                <span
                    className="flex-1 min-w-0 truncate"
                    style={{
                        fontSize: 13.5,
                        fontWeight: picked ? 800 : 700,
                        color: picked ? 'var(--cpm-text-primary)' : 'var(--cpm-text-tertiary)',
                    }}
                >
                    {picked?.label ?? CPM_LICENCE_EXCISE_COPY.SELECT_PLACEHOLDER}
                </span>
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--cpm-text-tertiary)"
                    strokeWidth="2.2"
                    className="shrink-0 ml-auto"
                >
                    <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            {open && typeof document !== 'undefined'
                ? createPortal(
                      <div
                          ref={menuRef}
                          role="listbox"
                          className="cpm-theme bg-white border border-[var(--cpm-border-default)] rounded-[12px] p-[5px] flex flex-col cpm-animate-pop-in max-h-[330px] overflow-y-auto overflow-x-hidden"
                          style={{
                              position: 'fixed',
                              top: menuPos?.top ?? -9999,
                              left: menuPos?.left ?? -9999,
                              width: menuPos?.width ?? 360,
                              zIndex: 80,
                              visibility: menuPos ? 'visible' : 'hidden',
                              boxShadow:
                                  '0 1px 2px rgba(14,8,88,.06), 0 24px 48px -14px rgba(14,8,88,.40)',
                          }}
                      >
                          {options.map((o, i) => {
                              const on = selectedId === o.id;
                              const firstUnlicensed =
                                  hasLicensed &&
                                  i === firstUnlicensedIndex &&
                                  firstUnlicensedIndex > 0;
                              return (
                                  <button
                                      key={o.id}
                                      type="button"
                                      role="option"
                                      aria-selected={on}
                                      onClick={() => {
                                          onChange(o.id);
                                          setOpen(false);
                                      }}
                                      className="flex items-center text-left w-full hover:bg-[var(--cpm-surface-subtle)]"
                                      style={{
                                          gap: 9,
                                          padding: firstUnlicensed ? '12px 11px 9px' : '9px 11px',
                                          borderRadius: firstUnlicensed ? '0 0 9px 9px' : 9,
                                          marginTop: firstUnlicensed ? 5 : 0,
                                          borderTop: firstUnlicensed
                                              ? '1px solid var(--cpm-border-default)'
                                              : undefined,
                                          background: on
                                              ? 'var(--cpm-purple-tint-7)'
                                              : o.licensed
                                                ? 'var(--cpm-purple-tint-5)'
                                                : 'transparent',
                                      }}
                                  >
                                      <span
                                          className="flex-1 min-w-0"
                                          style={{
                                              fontSize: 13,
                                              fontWeight: 800,
                                              letterSpacing: '-0.01em',
                                              color: on
                                                  ? 'var(--cpm-brand-purple)'
                                                  : 'var(--cpm-text-primary)',
                                          }}
                                      >
                                          {o.label}
                                      </span>
                                      {o.licensed ? (
                                          <span
                                              className="shrink-0 whitespace-nowrap inline-flex items-center rounded-full"
                                              style={{
                                                  padding: '3px 9px',
                                                  background: 'var(--cpm-purple-tint-13)',
                                                  border: '1px solid var(--cpm-purple-accent-border)',
                                                  color: 'var(--cpm-brand-navy)',
                                                  fontSize: 10,
                                                  fontWeight: 800,
                                                  letterSpacing: '0.03em',
                                              }}
                                          >
                                              {CPM_LICENCE_EXCISE_COPY.ON_STORE_LICENCE}
                                          </span>
                                      ) : null}
                                      {on ? (
                                          <svg
                                              width="13"
                                              height="13"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="var(--cpm-brand-purple)"
                                              strokeWidth="3"
                                              className="shrink-0"
                                          >
                                              <path
                                                  d="M20 6 9 17l-5-5"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                              />
                                          </svg>
                                      ) : null}
                                  </button>
                              );
                          })}
                      </div>,
                      document.body
                  )
                : null}
        </div>
    );
}
